import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getLabels, getLabel, getLabelsStats, getLabelSuggestions } from '../api/labels'
import { LabelFilters } from '../types/label'

export const labelsKeys = {
  all: ['labels'] as const,
  list: (filters: LabelFilters) => [...labelsKeys.all, 'list', filters] as const,
  detail: (name: string) => [...labelsKeys.all, 'detail', name] as const,
  stats: () => [...labelsKeys.all, 'stats'] as const,
}

export function useLabels(filters: LabelFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: labelsKeys.list(filters),
    queryFn: () => getLabels(filters),
    enabled: options?.enabled !== false,
  })
}

export function useLabel(name: string) {
  return useQuery({
    queryKey: labelsKeys.detail(name),
    queryFn: () => getLabel(name),
    enabled: !!name,
  })
}

export function useLabelsStats() {
  return useQuery({
    queryKey: labelsKeys.stats(),
    queryFn: () => getLabelsStats(),
  })
}

export function useLabelSuggestions(q: string, debounceMs = 200) {
  const [debounced, setDebounced] = useState(q)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), debounceMs)
    return () => clearTimeout(t)
  }, [q, debounceMs])

  return useQuery({
    queryKey: [...labelsKeys.all, 'suggest', debounced] as const,
    queryFn: () => getLabelSuggestions(debounced),
    enabled: debounced.trim().length >= 1,
    staleTime: 1000 * 30,
    placeholderData: [],
  })
}
